
import { AlertBanner, Button, Loading, Text, Checkbox } from "snap-design-system";
import { useUserCRUD } from "~/hooks/useUserCRUD";
import { useDialogActions } from "~/store/dialogStore";
import { useState, useCallback, useMemo } from "react";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { Check, X, LogOut, AlertTriangle } from "lucide-react";
import { useAuth } from "~/hooks/useAuth";
import { useNavigate } from "react-router";
import { parseMarkdownToHtml } from "~/helpers";
import { EulaModel } from "../../../domain/entities/eula.model";

export function EulaDialogContent() {
  const { eulaQuery } = useUserCRUD();
  const { data: eulaData, isLoading } = eulaQuery;

  const eulaModel = useMemo(() => {
    if (!eulaData) return null;
    return new EulaModel(eulaData);
  }, [eulaData]);

  const renderedHtml = useMemo(() => {
    if (!eulaModel || !eulaModel.isValid) {
      console.warn("Invalid EULA data");
      return "";
    }
    return parseMarkdownToHtml(eulaModel.content);
  }, [eulaModel]);

  return (
    <div className="max-h-96 overflow-y-auto">
      <Text
        variant="body-lg"
        className="mb-5 font-bold"
      >
        Para utilizar esta plataforma, você deve estar plenamente de acordo com os nossos termos de uso, abaixo relacionados:
      </Text>
      {isLoading ? (
        <div className="flex items-center justify-center p-8">
          <Loading size="md" />
        </div>
      ) :
        <div
          className="text-sm [&_h1]:text-lg [&_h1]:font-bold [&_h1]:mb-4 [&_h1]:mt-6 [&_h2]:text-base [&_h2]:font-bold [&_h2]:mb-3 [&_h2]:mt-5 [&_h3]:font-bold [&_h3]:mb-2 [&_h3]:mt-4 [&_p]:mb-4 [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:mb-4 [&_li]:mb-2 [&_hr]:my-4 [&_hr]:border-muted-foreground/30 [&_blockquote]:border-l-4 [&_blockquote]:border-muted-foreground/40 [&_blockquote]:pl-4 [&_blockquote]:my-3 [&_blockquote]:italic [&_strong]:font-bold"
          dangerouslySetInnerHTML={{ __html: renderedHtml || "Carregando termos..." }}
        />
      }
    </div>
  );
}

export function EulaDialogFooter() {
  const navigate = useNavigate();
  const { acceptTermsMutation, eulaQuery } = useUserCRUD();
  const { closeDialog, openDialog } = useDialogActions();
  const [isChecked, setIsChecked] = useState(false);
  const {
    logoutMutation: { mutateAsync: logout },
  } = useAuth();
  const { error, isFetching } = eulaQuery;

  const handleAccept = async () => {
    try {
      await acceptTermsMutation.mutateAsync();
      closeDialog();
    } catch (error) {
      console.error("Error accepting terms:", error);
    }
  };

  const handleDecline = () => {
    const handleConfirmDecline = () => {
      logout();
    };

    const handleCancelDecline = () => {
      openDialog({
        title: "Termos de Uso",
        icon: <AlertTriangle />,
        content: <EulaDialogContent />,
        footer: <EulaDialogFooter />,
        className: "max-w-4xl",
        preventOutsideClose: true,
        hideCloseButton: true,
        testId: "snap-terms-dialog",
        titleTestId: "snap-terms-title",
      });
    };

    openDialog({
      title: "Confirmação de Recusa",
      icon: <AlertTriangle />,
      content: (
        <EulaDeclineConfirmationDialog.Content
          onConfirm={handleConfirmDecline}
          onCancel={handleCancelDecline}
        />
      ),
      footer: (
        <EulaDeclineConfirmationDialog.Footer
          onConfirm={handleConfirmDecline}
          onCancel={handleCancelDecline}
        />
      ),
      className: "max-w-lg",
      preventOutsideClose: true,
      hideCloseButton: true,
    });
  };

  const handleCheckboxChange = (checked: boolean) => {
    setIsChecked(checked);
  };

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      setIsChecked(prev => !prev);
    }
  }, []);

  if (error) {
    console.error("Error fetching EULA:", error);
    navigate("/login", { replace: true });
  }

  return (
    <div className="flex flex-col gap-4 w-full items-end">
      <div className="flex items-center gap-2 self-start">
        <Checkbox
          variant="accent"
          id="accept-terms"
          checked={isChecked}
          onCheckedChange={handleCheckboxChange}
          onKeyDown={handleKeyDown}
          disabled={isFetching}
          data-test-id="snap-terms-accept-checkbox"
        />
        <label
          htmlFor="accept-terms"
          className="text-sm cursor-pointer"
          onKeyDown={handleKeyDown}
          tabIndex={0}
        >
          Declaro que li e concordo com os termos acima relacionados.
        </label>
      </div>
      <div className="flex gap-3">
        <Button
          onClick={handleDecline}
          icon={<X size={16} />}
          iconPosition="right"
        >
          Recusar
        </Button>
        <Button
          onClick={handleAccept}
          icon={acceptTermsMutation.isPending ? <AiOutlineLoading3Quarters size={16} /> : <Check size={16} />}
          iconPosition="right"
          disabled={!isChecked || acceptTermsMutation.isPending}
          data-test-id="snap-terms-accept-button"
        >
          Aceitar
        </Button>
      </div>
    </div>
  );
}

interface EulaDeclineConfirmationProps {
  onConfirm?: () => void;
  onCancel?: () => void;
}

interface EulaDeclineConfirmationFooterProps {
  onConfirm?: () => void;
  onCancel?: () => void;
}

function EulaDeclineConfirmation({ }: EulaDeclineConfirmationProps) {
  return (
    <div className="space-y-4">
      <AlertBanner
        variant="accent"
        icon={<LogOut className="size-5" />}
        title="Você confirma a recusa dos termos de uso?"
        message="Ao recusar os termos, você não poderá acessar a plataforma e suas funcionalidades, sendo redirecionado para a página de login."
      />
    </div>
  );
}

function EulaDeclineConfirmationFooter({ onConfirm, onCancel }: EulaDeclineConfirmationFooterProps) {
  return (
    <div className="flex gap-3 justify-end">
      <Button
        variant={"ghost"}
        onClick={onCancel}
      >
        Voltar
      </Button>
      <Button
        onClick={onConfirm}
        icon={<Check size={16} />}
        iconPosition="right"
      >
        Confirmar
      </Button>
    </div>
  );
}

export const EulaDeclineConfirmationDialog = {
  Content: EulaDeclineConfirmation,
  Footer: EulaDeclineConfirmationFooter
};

export const EulaDialog = {
  Content: EulaDialogContent,
  Footer: EulaDialogFooter,
};
